// 家用面板行事曆 —— 家人端（V-23，docs/panel/09-voice-calendar.md §5.1、§5.3、§6.3、§8、§9.2、§9.7、§9.8；VC1a、VC1b）。
// 這裡只放 API 呼叫與純邏輯（日期格子、RRULE 子集 ↔ 畫面、提醒選項、整串 / 單次的 If-Match、錯誤分類、時區換算、
// 能管行事曆的自動規則），畫面在 PanelCalendarPage.tsx / PanelCalendarCards.tsx。
//   - 所有時間都是**面板當地牆上時間**（floating，`YYYY-MM-DDTHH:mm`；全天 `YYYY-MM-DD`）；雲端用面板的 tz 解讀。
//   - 只有雲端展開重複：GET calendar 回展開好的 occurrence，網頁不自己算 RRULE（只把畫面選項組成子集字串）。
//   - 全天事件的 offset_min 從當天 09:00（面板當地）起算（09 §9.7 v0.8.2）；全天事件的「準時」也是 09:00（§9.8）。
//   - VC1b：家人代確認 calendar:ack（只收 done、via 由雲端判定 family）、確認紀錄（照 chain_id 接在一起照時間排）。
import { ApiError, apiFetch } from '../../lib/api';

// ── 型別 ──

export type CalCategory = 'med' | 'doctor' | 'birthday' | 'other';
export const CAL_CATEGORIES: CalCategory[] = ['med', 'doctor', 'birthday', 'other'];

/** {offset_min: -10080…0}（0 = 準時）或 {day_before: 1–7, at: "HH:mm"} */
export interface CalReminder {
  offset_min?: number;
  day_before?: number;
  at?: string;
}

export interface CalAck {
  result: string;
  at: string;
  via: string;
  by_name?: string;
  pending?: boolean;
}

/** GET calendar 的一次（家人端多帶編輯用欄位） */
export interface CalOccurrence {
  event_id: string;
  series_id?: string;
  recurrence_id: string;
  occurrence_start: string;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  location?: string;
  for_whom?: string;
  category: string;
  needs_ack: boolean;
  reminders_at: string[];
  source: string;
  created_by_name: string;
  ack?: CalAck;
  note?: string;
  duration_min?: number;
  reminders?: CalReminder[];
  rrule?: string;
  exdates?: string[];
  rev?: string;
  chain_id?: string;
  chain_rev?: string;
  /** 最後改的人（家人端；面板 / 語音改的 = 空，09 v0.8.3） */
  updated_by_name?: string;
  /** 這一列建立 / 最後改的時間（unix 微秒；家人端，09 v0.9.1） */
  created_at?: number;
  updated_at?: number;
}

export interface CalendarView {
  version: string;
  tz: string;
  /** 帶時差 ISO 8601（面板當地） */
  now: string;
  from: string;
  to: string;
  occurrences: CalOccurrence[];
  /** 家人端才有：settings.residents（「給誰」下拉用，09 v0.8.3） */
  residents: string[];
}

export interface CalEventBody {
  title?: string;
  start?: string;
  all_day?: boolean;
  duration_min?: number;
  location?: string;
  note?: string;
  rrule?: string;
  reminders?: CalReminder[];
  needs_ack?: boolean;
  for_whom?: string;
  category?: string;
  scope?: CalScope;
  recurrence_id?: string;
}

export type CalScope = 'this' | 'following' | 'all';

// ── 解析（雲端回什麼都不讓畫面壞掉） ──

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const optStr = (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined);

function parseReminders(v: unknown): CalReminder[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return v.flatMap((r): CalReminder[] => {
    const o = (r && typeof r === 'object' ? r : {}) as Record<string, unknown>;
    if (typeof o.offset_min === 'number') return [{ offset_min: o.offset_min }];
    if (typeof o.day_before === 'number' && typeof o.at === 'string') return [{ day_before: o.day_before, at: o.at }];
    return [];
  });
}

export function parseOccurrence(raw: unknown): CalOccurrence | null {
  const o = (raw && typeof raw === 'object' ? raw : null) as Record<string, unknown> | null;
  if (!o || typeof o.event_id !== 'string' || typeof o.start !== 'string') return null;
  const ack = o.ack && typeof o.ack === 'object' ? (o.ack as Record<string, unknown>) : null;
  return {
    event_id: o.event_id,
    series_id: optStr(o.series_id),
    recurrence_id: str(o.recurrence_id) || o.start,
    occurrence_start: str(o.occurrence_start) || o.start,
    title: str(o.title),
    start: o.start,
    end: str(o.end) || o.start,
    all_day: o.all_day === true,
    location: optStr(o.location),
    for_whom: optStr(o.for_whom),
    category: str(o.category) || 'other',
    needs_ack: o.needs_ack === true,
    reminders_at: Array.isArray(o.reminders_at) ? o.reminders_at.filter((x): x is string => typeof x === 'string') : [],
    source: str(o.source),
    created_by_name: str(o.created_by_name),
    ack: ack ? { result: str(ack.result), at: str(ack.at), via: str(ack.via), by_name: optStr(ack.by_name), pending: ack.pending === true } : undefined,
    note: optStr(o.note),
    duration_min: typeof o.duration_min === 'number' ? o.duration_min : undefined,
    reminders: parseReminders(o.reminders),
    rrule: optStr(o.rrule),
    exdates: Array.isArray(o.exdates) ? o.exdates.filter((x): x is string => typeof x === 'string') : undefined,
    rev: optStr(o.rev),
    chain_id: optStr(o.chain_id),
    chain_rev: optStr(o.chain_rev),
    updated_by_name: optStr(o.updated_by_name),
    created_at: typeof o.created_at === 'number' ? o.created_at : undefined,
    updated_at: typeof o.updated_at === 'number' ? o.updated_at : undefined,
  };
}

export function parseCalendarView(raw: unknown): CalendarView {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const occ = Array.isArray(o.occurrences) ? o.occurrences.map(parseOccurrence).filter((x): x is CalOccurrence => x !== null) : [];
  const residents = Array.isArray(o.residents) ? o.residents.filter((x): x is string => typeof x === 'string' && x !== '') : [];
  return { version: str(o.version), tz: str(o.tz), now: str(o.now), from: str(o.from), to: str(o.to), occurrences: occ, residents };
}

// ── API（行事曆家人：主人 + 能管行事曆的家人） ──

const calBase = (panelId: string) => `/v3/panels/${encodeURIComponent(panelId)}/calendar`;
const quoteTag = (v: string) => `"${v}"`;

/** from / to = 面板當地日期（含兩端；沒帶 = 今天 ~ +14 天；≤ 62 天） */
export async function getPanelCalendar(panelId: string, from?: string, to?: string): Promise<CalendarView> {
  return parseCalendarView((await apiFetch<unknown>(calBase(panelId), { query: { from, to } })).data);
}

export async function createCalEvent(panelId: string, body: CalEventBody): Promise<void> {
  await apiFetch(`${calBase(panelId)}/events`, { method: 'POST', body });
}

/** If-Match：不重複 / scope=this = 那一列 rev；following / all = chain_rev（ifMatchFor 算） */
export async function patchCalEvent(panelId: string, eventId: string, ifMatch: string, body: CalEventBody): Promise<void> {
  await apiFetch(`${calBase(panelId)}/events/${encodeURIComponent(eventId)}`, { method: 'PATCH', body, headers: { 'If-Match': quoteTag(ifMatch) } });
}

/** DELETE 的 scope / recurrence_id 放 query、不帶 body；成功 204 */
/** 家人代確認（§6.3、§9.8）：{event_id = series_id ?? event_id, recurrence_id, result: "done"}；via / acked_at 由雲端定 */
export interface CalAckResult {
  event_id: string;
  recurrence_id: string;
  ack: CalAck | null;
  /** false = 這一次已經確認過（再送 200、不改） */
  changed: boolean;
  version: string;
}

export async function ackCalOccurrence(panelId: string, o: Pick<CalOccurrence, 'event_id' | 'series_id' | 'recurrence_id'>): Promise<CalAckResult> {
  const raw = (await apiFetch<unknown>(`${calBase(panelId)}:ack`, {
    method: 'POST', body: { event_id: ackEventId(o), recurrence_id: o.recurrence_id, result: 'done' },
  })).data;
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const a = r.ack && typeof r.ack === 'object' ? (r.ack as Record<string, unknown>) : null;
  return {
    event_id: str(r.event_id), recurrence_id: str(r.recurrence_id), changed: r.changed !== false, version: str(r.version),
    ack: a ? { result: str(a.result), at: str(a.at), via: str(a.via), by_name: optStr(a.by_name), pending: a.pending === true } : null,
  };
}

export async function deleteCalEvent(panelId: string, eventId: string, ifMatch: string, scope?: CalScope, recurrenceId?: string): Promise<void> {
  await apiFetch(`${calBase(panelId)}/events/${encodeURIComponent(eventId)}`, {
    method: 'DELETE',
    headers: { 'If-Match': quoteTag(ifMatch) },
    query: scope ? { scope, recurrence_id: recurrenceId } : undefined,
  });
}

// ── 日期（面板當地日期字串，用 UTC 計算避免瀏覽器時區影響） ──

export type CalViewMode = 'month' | 'week' | 'day';

const pad = (n: number) => String(n).padStart(2, '0');
export function toDateStr(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
export function parseDateStr(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}
export function addDays(s: string, n: number): string {
  const d = parseDateStr(s);
  d.setUTCDate(d.getUTCDate() + n);
  return toDateStr(d);
}
/** 月份加減；日固定成 1 號（當月份錨點用） */
export function addMonths(s: string, n: number): string {
  const d = parseDateStr(s);
  return toDateStr(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)));
}
/** 0 = 星期日 */
export function weekdayOf(s: string): number {
  return parseDateStr(s).getUTCDay();
}
export const wallDate = (wall: string) => wall.slice(0, 10);
export const wallTime = (wall: string) => (wall.length >= 16 ? wall.slice(11, 16) : '');
/** 回應的 now（帶時差 ISO）→ 面板當地牆上時間 YYYY-MM-DDTHH:mm */
export const nowWall = (now: string) => now.slice(0, 16);

/** 週日開始的 7 天 */
export function weekDays(anchor: string): string[] {
  const first = addDays(anchor, -weekdayOf(anchor));
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

/** 月檢視 6 週 × 7 天（週日開始；含前後月補齊的日子） */
export function monthGrid(anchor: string): string[] {
  const first = `${anchor.slice(0, 7)}-01`;
  const start = addDays(first, -weekdayOf(first));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function viewRange(mode: CalViewMode, anchor: string): { from: string; to: string } {
  if (mode === 'day') return { from: anchor, to: anchor };
  const days = mode === 'week' ? weekDays(anchor) : monthGrid(anchor);
  return { from: days[0], to: days[days.length - 1] };
}

export function shiftAnchor(mode: CalViewMode, anchor: string, dir: 1 | -1): string {
  if (mode === 'day') return addDays(anchor, dir);
  if (mode === 'week') return addDays(anchor, 7 * dir);
  return addMonths(anchor, dir);
}

/** 依日期分組；同一天全天的排前面、再照時間 */
export function groupByDay(occs: CalOccurrence[]): Record<string, CalOccurrence[]> {
  const out: Record<string, CalOccurrence[]> = {};
  for (const o of occs) (out[wallDate(o.start)] ??= []).push(o);
  for (const k of Object.keys(out)) {
    out[k].sort((a, b) => (a.all_day !== b.all_day ? (a.all_day ? -1 : 1) : a.start.localeCompare(b.start) || a.title.localeCompare(b.title)));
  }
  return out;
}

/** 同一串（chain_id）在畫面上是同一件事：詳細頁的規則取這串裡最晚開始的那一次所在的段（§5.3） */
export function latestOfChain(occs: CalOccurrence[], o: CalOccurrence): CalOccurrence {
  if (!o.chain_id) return o;
  return occs.filter((x) => x.chain_id === o.chain_id && x.rrule).reduce((best, x) => (x.start > best.start ? x : best), o);
}

// ── 時區（家人在海外：另顯示換算後的本地時間） ──

function tzOffsetMs(tz: string, instant: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second')) - Math.floor(instant / 1000) * 1000;
}

/** 面板當地牆上時間 → 絕對時間（ms）；tz 不合法回 null */
export function wallToInstant(wall: string, tz: string): number | null {
  try {
    const [d, t = '00:00'] = wall.split('T');
    const [y, m, day] = d.split('-').map(Number);
    const [hh, mm] = t.split(':').map(Number);
    const guess = Date.UTC(y, m - 1, day, hh, mm);
    let inst = guess - tzOffsetMs(tz, guess);
    inst = guess - tzOffsetMs(tz, inst); // 第二次修正日光節約交界
    return inst;
  } catch {
    return null;
  }
}

/** 絕對時間 → 某時區的牆上時間 YYYY-MM-DDTHH:mm */
export function instantToWall(instant: number, tz: string): string {
  const d = new Date(instant + tzOffsetMs(tz, instant));
  return `${toDateStr(d)}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

export function viewerTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** 面板時間換成看的人那邊的時間；兩邊一樣（同時差）回 null（不用多顯示） */
export function viewerWall(wall: string, panelTz: string, myTz: string = viewerTz()): string | null {
  if (!panelTz || wall.length < 16) return null;
  const inst = wallToInstant(wall, panelTz);
  if (inst === null) return null;
  try {
    const mine = instantToWall(inst, myTz);
    return mine === wall ? null : mine;
  } catch {
    return null;
  }
}

// ── 重複規則（RRULE 子集 ↔ 畫面選項，§5.1 表） ──

export type RepeatKind = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
export type RepeatEnd = 'never' | 'until' | 'count';
export const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export interface RepeatSpec {
  kind: RepeatKind;
  /** 每 N 天 / 每 N 週 */
  interval: number;
  /** 每週幾（WEEKDAY_CODES） */
  byday: string[];
  /** 每月幾號：1–31；-1 = 每月最後一天 */
  monthday: number;
  end: RepeatEnd;
  /** YYYY-MM-DD（面板當地日期，含這天） */
  until: string;
  count: number;
}

export function defaultRepeat(start: string): RepeatSpec {
  const d = wallDate(start);
  return { kind: 'none', interval: 1, byday: [WEEKDAY_CODES[weekdayOf(d)]], monthday: Number(d.slice(8, 10)) || 1, end: 'never', until: '', count: 10 };
}

/** 畫面選項 → RRULE（不重複 = ""）。UNTIL 不帶 Z（floating）；全天只寫日期，有時刻的寫那天 23:59:00。 */
export function buildRRule(spec: RepeatSpec, start: string, allDay: boolean): string {
  if (spec.kind === 'none') return '';
  const parts: string[] = [];
  const iv = Math.max(1, Math.min(99, Math.floor(spec.interval || 1)));
  switch (spec.kind) {
    case 'daily':
      parts.push('FREQ=DAILY');
      if (iv > 1) parts.push(`INTERVAL=${iv}`);
      break;
    case 'weekly': {
      parts.push('FREQ=WEEKLY');
      if (iv > 1) parts.push(`INTERVAL=${iv}`);
      const days = WEEKDAY_CODES.filter((c) => spec.byday.includes(c));
      parts.push(`BYDAY=${(days.length ? days : [WEEKDAY_CODES[weekdayOf(wallDate(start))]]).join(',')}`);
      break;
    }
    case 'monthly': {
      parts.push('FREQ=MONTHLY');
      const md = spec.monthday;
      if (md >= 1 && md <= 28) parts.push(`BYMONTHDAY=${md}`);
      else if (md === 29) parts.push('BYMONTHDAY=28,29', 'BYSETPOS=-1');
      else if (md === 30) parts.push('BYMONTHDAY=28,29,30', 'BYSETPOS=-1');
      else parts.push('BYMONTHDAY=-1'); // 31 號 / 每月最後一天
      break;
    }
    case 'yearly':
      parts.push('FREQ=YEARLY');
      if (wallDate(start).slice(5) === '02-29') parts.push('BYMONTH=2', 'BYMONTHDAY=28,29', 'BYSETPOS=-1'); // 平年排 2/28
      break;
  }
  if (spec.end === 'until' && /^\d{4}-\d{2}-\d{2}$/.test(spec.until)) {
    const u = spec.until.replace(/-/g, '');
    parts.push(`UNTIL=${allDay ? u : `${u}T235900`}`);
  } else if (spec.end === 'count') {
    parts.push(`COUNT=${Math.max(1, Math.min(500, Math.floor(spec.count || 1)))}`);
  }
  return parts.join(';');
}

/** RRULE → 畫面選項；不在畫面能表達的範圍回 null（詳細頁照原字串顯示、編輯時只能整個換掉） */
export function parseRRule(rrule: string | undefined, start: string): RepeatSpec | null {
  const spec = defaultRepeat(start);
  if (!rrule) return spec;
  const kv: Record<string, string> = {};
  for (const part of rrule.replace(/^RRULE:/i, '').toUpperCase().split(';')) {
    const [k, v] = part.split('=');
    if (!k || v === undefined) return null;
    kv[k] = v;
  }
  if (kv.INTERVAL) spec.interval = Number(kv.INTERVAL) || 1;
  switch (kv.FREQ) {
    case 'DAILY':
      spec.kind = 'daily';
      break;
    case 'WEEKLY':
      spec.kind = 'weekly';
      if (kv.BYDAY) spec.byday = kv.BYDAY.split(',');
      break;
    case 'MONTHLY':
      spec.kind = 'monthly';
      if (kv.INTERVAL && spec.interval !== 1) return null;
      if (kv.BYMONTHDAY === '-1') spec.monthday = -1;
      else if (kv.BYMONTHDAY === '28,29' && kv.BYSETPOS === '-1') spec.monthday = 29;
      else if (kv.BYMONTHDAY === '28,29,30' && kv.BYSETPOS === '-1') spec.monthday = 30;
      else if (kv.BYMONTHDAY && !kv.BYSETPOS) spec.monthday = Number(kv.BYMONTHDAY);
      break;
    case 'YEARLY':
      spec.kind = 'yearly';
      if (kv.INTERVAL && spec.interval !== 1) return null;
      break;
    default:
      return null;
  }
  if (kv.UNTIL) {
    const m = /^(\d{4})(\d{2})(\d{2})/.exec(kv.UNTIL);
    if (!m) return null;
    spec.end = 'until';
    spec.until = `${m[1]}-${m[2]}-${m[3]}`;
  } else if (kv.COUNT) {
    spec.end = 'count';
    spec.count = Number(kv.COUNT) || 1;
  }
  return spec;
}

/** 規則的白話描述：i18n key + 參數（星期幾由畫面翻譯 panelCal.wd.*） */
export function repeatLabel(spec: RepeatSpec): { key: string; params: Record<string, unknown> } {
  switch (spec.kind) {
    case 'none':
      return { key: 'panelCal.rep.none', params: {} };
    case 'daily':
      return spec.interval > 1 ? { key: 'panelCal.rep.everyNDays', params: { n: spec.interval } } : { key: 'panelCal.rep.daily', params: {} };
    case 'weekly':
      return { key: spec.interval > 1 ? 'panelCal.rep.everyNWeeksOn' : 'panelCal.rep.weeklyOn', params: { n: spec.interval, days: WEEKDAY_CODES.filter((c) => spec.byday.includes(c)) } };
    case 'monthly':
      return spec.monthday === -1 ? { key: 'panelCal.rep.monthlyLast', params: {} } : { key: 'panelCal.rep.monthlyOn', params: { d: spec.monthday } };
    case 'yearly':
      return { key: 'panelCal.rep.yearly', params: {} };
  }
}

/** 兩個 RRULE 的「形狀」是否不同（頻率、星期幾、每月幾號、間隔、結束）——不同 = 改了重複規則（單次修改會清掉） */
export function rruleShapeChanged(before: string | undefined, after: string): boolean {
  const norm = (s: string | undefined) => (s ?? '').toUpperCase().split(';').filter(Boolean).sort().join(';');
  return norm(before) !== norm(after);
}

// ── 提醒 ──

export const CAL_MAX_REMINDERS = 3;
/** 全天事件 offset_min 的錨點：當天 09:00（面板當地；09 §9.7 v0.8.2） */
export const ALL_DAY_ANCHOR_MIN = 9 * 60;

export const TIMED_REMINDER_OPTIONS: CalReminder[] = [
  { offset_min: 0 }, { offset_min: -10 }, { offset_min: -30 }, { offset_min: -60 }, { offset_min: -120 }, { offset_min: -1440 }, { day_before: 1, at: '20:00' },
];
export const ALL_DAY_REMINDER_OPTIONS: CalReminder[] = [
  { offset_min: 0 }, { offset_min: -60 }, { offset_min: -1440 }, { day_before: 1, at: '20:00' },
];

export function reminderId(r: CalReminder): string {
  return r.offset_min !== undefined ? `o${r.offset_min}` : `d${r.day_before}@${r.at}`;
}

/** 全天事件的 offset → 「幾天前 + 幾點」（從當天 09:00 起算） */
export function allDayReminderClock(offsetMin: number): { daysBefore: number; time: string } {
  const total = ALL_DAY_ANCHOR_MIN + offsetMin;
  const dayShift = Math.floor(total / 1440);
  const mins = total - dayShift * 1440;
  return { daysBefore: dayShift === 0 ? 0 : -dayShift, time: `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}` };
}

export function reminderLabel(r: CalReminder, allDay: boolean): { key: string; params: Record<string, unknown> } {
  if (r.day_before !== undefined) {
    return r.day_before === 1 ? { key: 'panelCal.rem.dayBefore1', params: { at: r.at } } : { key: 'panelCal.rem.dayBeforeN', params: { n: r.day_before, at: r.at } };
  }
  const off = r.offset_min ?? 0;
  if (allDay) {
    const c = allDayReminderClock(off);
    if (c.daysBefore === 0) return { key: 'panelCal.rem.allDaySameDay', params: { time: c.time } };
    if (c.daysBefore === 1) return { key: 'panelCal.rem.allDayDayBefore', params: { time: c.time } };
    return { key: 'panelCal.rem.allDayNDaysBefore', params: { n: c.daysBefore, time: c.time } };
  }
  if (off === 0) return { key: 'panelCal.rem.onTime', params: {} };
  const m = -off;
  if (m % 1440 === 0) return { key: 'panelCal.rem.daysBefore', params: { n: m / 1440 } };
  if (m % 60 === 0) return { key: 'panelCal.rem.hoursBefore', params: { n: m / 60 } };
  return { key: 'panelCal.rem.minutesBefore', params: { n: m } };
}

/** 要確認的事件一定有準時那次（雲端也會補，§4.2）；最多 3 個 */
export function withAckReminder(rs: CalReminder[], needsAck: boolean): CalReminder[] {
  if (!needsAck || rs.some((r) => r.offset_min === 0)) return rs.slice(0, CAL_MAX_REMINDERS);
  return [{ offset_min: 0 }, ...rs].slice(0, CAL_MAX_REMINDERS);
}

/** 新增時標題含「藥」預設要長輩確認（§5.1） */
export function defaultNeedsAck(title: string): boolean {
  return title.includes('藥');
}

// ── 編輯表單 ↔ body ──

export interface CalDraft {
  title: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm（全天時忽略） */
  time: string;
  allDay: boolean;
  durationMin: number;
  location: string;
  note: string;
  repeat: RepeatSpec;
  /** RRULE 不在畫面子集時保留原字串（沒動重複設定就照原樣） */
  rawRRule: string | null;
  reminders: CalReminder[];
  needsAck: boolean;
  forWhom: string;
  /** '' = 讓雲端依標題猜 */
  category: string;
}

export function draftStart(d: CalDraft): string {
  return d.allDay ? d.date : `${d.date}T${d.time || '09:00'}`;
}

export function draftRRule(d: CalDraft): string {
  if (d.rawRRule !== null) return d.rawRRule;
  return buildRRule(d.repeat, draftStart(d), d.allDay);
}

export function newDraft(date: string): CalDraft {
  return {
    title: '', date, time: '09:00', allDay: false, durationMin: 60, location: '', note: '',
    repeat: defaultRepeat(date), rawRRule: null, reminders: [{ offset_min: 0 }], needsAck: false, forWhom: '', category: '',
  };
}

/** occurrence → 編輯表單（重複規則取同一串最新那段的，§5.3） */
export function draftFromOccurrence(o: CalOccurrence, ruleSource: CalOccurrence = o): CalDraft {
  const rr = ruleSource.rrule ?? '';
  const spec = parseRRule(rr, o.start);
  return {
    title: o.title, date: wallDate(o.start), time: wallTime(o.start) || '09:00', allDay: o.all_day,
    durationMin: o.duration_min ?? 60, location: o.location ?? '', note: o.note ?? '',
    repeat: spec ?? defaultRepeat(o.start), rawRRule: spec ? null : rr,
    reminders: o.reminders ?? [], needsAck: o.needs_ack, forWhom: o.for_whom ?? '', category: o.category,
  };
}

/** 客戶端先擋的欄位錯誤（其餘以雲端為準） */
export function draftError(d: CalDraft): string | null {
  const t = d.title.trim();
  if (t.length < 1 || [...t].length > 40) return 'panelCal.err.title';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date)) return 'panelCal.err.date';
  if (!d.allDay && !/^\d{2}:\d{2}$/.test(d.time)) return 'panelCal.err.time';
  if (!d.allDay && (d.durationMin < 5 || d.durationMin > 720)) return 'panelCal.err.duration';
  if ([...d.location].length > 40) return 'panelCal.err.location';
  if ([...d.note].length > 200) return 'panelCal.err.note';
  if (d.reminders.length > CAL_MAX_REMINDERS) return 'panelCal.err.reminders';
  if (d.repeat.kind !== 'none' && d.rawRRule === null && d.repeat.end === 'until' && d.repeat.until && d.repeat.until < d.date) return 'panelCal.err.until';
  return null;
}

/** 新增的 body（空欄位不送） */
export function createBody(d: CalDraft): CalEventBody {
  const b: CalEventBody = { title: d.title.trim(), start: draftStart(d), all_day: d.allDay, reminders: withAckReminder(d.reminders, d.needsAck), needs_ack: d.needsAck };
  if (!d.allDay) b.duration_min = d.durationMin;
  if (d.location.trim()) b.location = d.location.trim();
  if (d.note.trim()) b.note = d.note.trim();
  const rr = draftRRule(d);
  if (rr) b.rrule = rr;
  if (d.forWhom) b.for_whom = d.forWhom;
  if (d.category) b.category = d.category;
  return b;
}

export interface DraftChanges {
  body: CalEventBody;
  allDayChanged: boolean;
  rruleChanged: boolean;
  rruleCleared: boolean;
  startChanged: boolean;
}

/** 修改：只送改了的欄位（整串操作不能改 all_day、不能清 rrule；scope=this 不能帶 rrule） */
export function patchChanges(before: CalDraft, after: CalDraft): DraftChanges {
  const body: CalEventBody = {};
  const s = (v: string) => v.trim();
  if (s(before.title) !== s(after.title)) body.title = s(after.title);
  const startBefore = draftStart(before);
  const startAfter = draftStart(after);
  const allDayChanged = before.allDay !== after.allDay;
  if (allDayChanged) body.all_day = after.allDay;
  const startChanged = startBefore !== startAfter;
  if (startChanged || allDayChanged) body.start = startAfter;
  if (!after.allDay && (before.durationMin !== after.durationMin || allDayChanged)) body.duration_min = after.durationMin;
  if (s(before.location) !== s(after.location)) body.location = s(after.location);
  if (s(before.note) !== s(after.note)) body.note = s(after.note);
  const rrBefore = draftRRule(before);
  const rrAfter = draftRRule(after);
  const rruleChanged = rruleShapeChanged(rrBefore, rrAfter);
  if (rruleChanged) body.rrule = rrAfter;
  const remA = withAckReminder(after.reminders, after.needsAck);
  if (JSON.stringify(withAckReminder(before.reminders, before.needsAck)) !== JSON.stringify(remA)) body.reminders = remA;
  if (before.needsAck !== after.needsAck) body.needs_ack = after.needsAck;
  if (before.forWhom !== after.forWhom) body.for_whom = after.forWhom;
  if (before.category !== after.category && after.category) body.category = after.category;
  return { body, allDayChanged, rruleChanged, rruleCleared: rruleChanged && rrBefore !== '' && rrAfter === '', startChanged };
}

// ── 重複事件：範圍與 If-Match ──

export function isRecurringOcc(o: Pick<CalOccurrence, 'rrule' | 'series_id'>): boolean {
  return !!o.rrule || !!o.series_id;
}

/** 不重複 / 只改這一次 = 那一列的 rev；這次以後 / 全部 = chain_rev（09 §9.7） */
export function ifMatchFor(o: CalOccurrence, scope?: CalScope): string {
  if (!isRecurringOcc(o) || !scope || scope === 'this') return o.rev ?? '';
  return o.chain_rev ?? o.rev ?? '';
}

/** 依改了什麼決定能選哪些範圍；空陣列 = 不重複的事件（不用問）；null = 這樣改不行（例如清掉重複規則） */
export function allowedScopes(o: CalOccurrence, c: Pick<DraftChanges, 'allDayChanged' | 'rruleChanged' | 'rruleCleared'>): CalScope[] | null {
  if (!isRecurringOcc(o)) return [];
  if (c.rruleCleared) return null; // 整串不能清 rrule，請刪這次以後再新增
  if (c.allDayChanged && c.rruleChanged) return null;
  if (c.allDayChanged) return ['this']; // 整串不能改 all_day
  if (c.rruleChanged) return ['following', 'all']; // 單次覆寫沒有重複規則
  return ['this', 'following', 'all'];
}

/** 確認框小字（§5.3）：改全部 / 這次以後 →「從下一次開始」；今天這次還沒到但改到已經過去的時刻 →「今天照原本時間」 */
export function scopeNoteKey(o: CalOccurrence, scope: CalScope, now: string, newStart: string | undefined): string | null {
  if (scope === 'this') return null;
  const today = wallDate(now);
  if (wallDate(o.start) === today && o.start > now && newStart && newStart.length >= 16 && newStart < now) return 'panelCal.scope.todayKeeps';
  return 'panelCal.scope.fromNext';
}

// ── 錯誤 ──

export type CalErr = 'forbidden' | 'notFound' | 'gone' | 'conflict' | 'full' | 'rrule' | 'range' | 'field' | 'precondition' | 'occurrence' | 'unavailable' | 'network' | 'generic';

export function calErr(e: unknown): { kind: CalErr; field?: string } {
  if (!(e instanceof ApiError)) return { kind: 'network' };
  switch (e.code) {
    case 'CALENDAR_FORBIDDEN':
    case 'FORBIDDEN':
      return { kind: 'forbidden' };
    case 'PANEL_NOT_FOUND':
      return { kind: 'notFound' };
    case 'NOT_FOUND':
      return { kind: 'gone' };
    case 'CALENDAR_CONFLICT':
      return { kind: 'conflict' };
    case 'CALENDAR_FULL':
      return { kind: 'full' };
    case 'INVALID_RRULE':
      return { kind: 'rrule' };
    case 'INVALID_RANGE':
      return { kind: 'range' };
    case 'PRECONDITION_REQUIRED':
      return { kind: 'precondition' };
    case 'INVALID_OCCURRENCE':
      return { kind: 'occurrence' }; // 代確認：不在時間窗（開始前 10 分鐘 ~ 開始後 6 小時）或那一次已經被刪
    case 'INVALID_REQUEST': {
      const f = e.details?.field;
      return { kind: 'field', field: typeof f === 'string' ? f : undefined };
    }
    case 'NETWORK_UNAVAILABLE':
      return { kind: 'network' };
  }
  if (e.status === 503) return { kind: 'unavailable' };
  if (e.status === 403) return { kind: 'forbidden' };
  return { kind: 'generic' };
}

const FIELD_KEYS: Record<string, string> = {
  title: 'panelCal.err.title', start: 'panelCal.err.date', duration_min: 'panelCal.err.duration', location: 'panelCal.err.location',
  note: 'panelCal.err.note', reminders: 'panelCal.err.reminders', for_whom: 'panelCal.err.forWhom', category: 'panelCal.err.category',
  rrule: 'panelCal.err_rrule', all_day: 'panelCal.err.allDayChain', scope: 'panelCal.err.scope', recurrence_id: 'panelCal.err.scope',
};

/** 錯誤 → 文案 key */
export function calErrKey(e: unknown): string {
  const { kind, field } = calErr(e);
  if (kind === 'field') return (field && FIELD_KEYS[field]) || 'panelCal.err.field';
  return `panelCal.err_${kind}`;
}

// ── 主人：長輩名字、叫法、能管行事曆（§5.3、§8） ──

export const RESIDENTS_MAX = 4;
export const CALL_NAMES_MAX = 3;
export const NAME_MAX_LEN = 10;

export interface CalSettings {
  residents: string[];
  callNames: Record<string, string[]>;
  /** null = 自動（主人 + 設過叫法的家人） */
  managers: string[] | null;
  /** 雲端算好的行事曆家人（含主人） */
  calendarFamily: string[];
}

const strList = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

export function parseCalSettings(raw: Record<string, unknown> | null | undefined): CalSettings {
  const cn: Record<string, string[]> = {};
  const m = raw?.family_call_names;
  if (m && typeof m === 'object' && !Array.isArray(m)) {
    for (const [k, v] of Object.entries(m as Record<string, unknown>)) {
      const l = strList(v);
      if (l.length) cn[k] = l;
    }
  }
  return {
    residents: strList(raw?.residents),
    callNames: cn,
    managers: Array.isArray(raw?.calendar_managers) ? strList(raw?.calendar_managers) : null,
    calendarFamily: strList(raw?.calendar_family),
  };
}

/** 同雲端 calendarFamily：主人 + (managers ?? 設過叫法的家人) ∩ 目前家人 */
export function effectiveManagers(owner: string, family: string[], callNames: Record<string, string[]>, managers: string[] | null): string[] {
  const pick = managers ?? family.filter((u) => (callNames[u] ?? []).length > 0);
  return [owner, ...family.filter((u) => u !== owner && pick.includes(u))];
}

/** 改任何一列 → 整組存成明確清單（之後新設叫法不再自動勾，§5.3）；主人不列 */
export function toggleManager(owner: string, family: string[], callNames: Record<string, string[]>, managers: string[] | null, uid: string, on: boolean): string[] {
  const cur = effectiveManagers(owner, family, callNames, managers).filter((u) => u !== owner);
  const next = on ? [...cur, uid] : cur.filter((u) => u !== uid);
  return family.filter((u) => u !== owner && next.includes(u));
}

/** 「兒子、阿明」→ ['兒子', '阿明']（逗號、頓號、空白都可以分） */
export function splitNames(text: string): string[] {
  const out: string[] = [];
  for (const n of text.split(/[,，、;；\s]+/)) {
    const v = n.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

export function namesError(names: string[], maxN: number): 'tooMany' | 'tooLong' | null {
  if (names.length > maxN) return 'tooMany';
  if (names.some((n) => [...n].length > NAME_MAX_LEN)) return 'tooLong';
  return null;
}

export function calSettingsBody(residents: string[], callNames: Record<string, string[]>, managers: string[] | null): Record<string, unknown> {
  const cn: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(callNames)) if (v.length) cn[k] = v;
  return { residents, family_call_names: cn, calendar_managers: managers };
}

/** 「給誰」下拉的選項：residents；事件原本的 for_whom 已經不在名單裡也留著（不要一打開就被清掉） */
export function forWhomOptions(residents: string[], current: string): string[] {
  return current && !residents.includes(current) ? [...residents, current] : residents;
}

/** 409 CALENDAR_CONFLICT 的 details.latest → 最後改的人（不重複 / scope=this 是一列；整串是 events[]，取最後改的那段） */
export function conflictByName(e: unknown): string {
  if (!(e instanceof ApiError)) return '';
  const latest = e.details?.latest as Record<string, unknown> | undefined;
  if (!latest || typeof latest !== 'object') return '';
  if (typeof latest.updated_by_name === 'string') return latest.updated_by_name;
  const evs = Array.isArray(latest.events) ? (latest.events as Record<string, unknown>[]) : [];
  let best: Record<string, unknown> | null = null;
  for (const x of evs) {
    if (x && typeof x === 'object' && (best === null || Number(x.updated_at ?? 0) > Number(best.updated_at ?? 0))) best = x;
  }
  return best && typeof best.updated_by_name === 'string' ? best.updated_by_name : '';
}

// ── VC1b：代確認與確認紀錄（§5.3、§6.3、§9.8） ──

/** calendar:ack 的 event_id：系列用 series_id（單次覆寫列也對得到），不重複的就是自己 */
export function ackEventId(o: Pick<CalOccurrence, 'event_id' | 'series_id'>): string {
  return o.series_id ?? o.event_id;
}

/** 牆上時間 → 分鐘數（當 UTC 算，只拿來比大小 / 相減；時區由雲端 now 的牆上時間對齊） */
export function wallMinutes(wall: string): number {
  const d = parseDateStr(wall);
  const t = wallTime(wall);
  const [hh, mm] = t ? t.split(':').map(Number) : [0, 0];
  return Math.floor(d.getTime() / 60000) + hh * 60 + mm;
}

/** 「準時」的牆上時間：有時刻 = 開始；全天 = 當天 09:00（§9.8） */
export function dueWall(o: Pick<CalOccurrence, 'start' | 'all_day'>): string {
  return o.all_day ? `${wallDate(o.start)}T09:00` : o.start;
}

export const ACK_EARLY_MIN = 10;
export const ACK_LATE_MIN = 6 * 60;
export const UNACKED_AFTER_MIN = 30;

/** 這一次已經確認（done；待核准時按的也算按過，但 C4 照發，§6.3） */
export function isAckDone(o: Pick<CalOccurrence, 'ack'>): boolean {
  return o.ack?.result === 'done';
}

/** 家人能不能代確認：要確認的事件、還沒 done、在 done 的時間窗內 [準時 − 10 分鐘, 準時 + 6 小時]（§6.3） */
export function canFamilyAck(o: CalOccurrence, now: string): boolean {
  if (!o.needs_ack || isAckDone(o) || !now) return false;
  const d = wallMinutes(nowWall(now)) - wallMinutes(dueWall(o));
  return d >= -ACK_EARLY_MIN && d <= ACK_LATE_MIN;
}

/** C4 的狀態（同雲端 §6.4）：準時 + 30 分鐘 ~ + 6 小時、還沒 done（待核准時按的 done 不算）→ normal | offline | pending；其他 null */
export function unackedVariant(o: CalOccurrence, now: string, online = true): 'normal' | 'offline' | 'pending' | null {
  if (!o.needs_ack || !now) return null;
  const d = wallMinutes(nowWall(now)) - wallMinutes(dueWall(o));
  if (d < UNACKED_AFTER_MIN || d > ACK_LATE_MIN) return null;
  if (isAckDone(o)) return o.ack?.pending ? 'pending' : null;
  return online ? 'normal' : 'offline';
}

/** 代確認按鈕：［阿嬤說吃過了］/［長輩說吃過了］；非吃藥類「…說做好了」（§5.3 v0.4） */
export function ackLabel(o: Pick<CalOccurrence, 'for_whom' | 'category'>): { key: string; params: Record<string, unknown> } {
  const med = o.category === 'med';
  if (o.for_whom) return { key: med ? 'panelCal.ack.medFor' : 'panelCal.ack.doneFor', params: { name: o.for_whom } };
  return { key: med ? 'panelCal.ack.med' : 'panelCal.ack.done', params: {} };
}

/** 確認紀錄一行的文案：「8:05 按了已經吃過了」「語音確認」「大明代確認」「面板在還沒核准的網路上確認」 */
export function ackRecordParts(a: CalAck, category: string): { key: string; params: Record<string, unknown> }[] {
  const time = a.at.length >= 16 ? a.at.slice(11, 16) : '';
  const out: { key: string; params: Record<string, unknown> }[] = [];
  if (a.result === 'done' && a.via === 'family') {
    out.push({ key: a.by_name ? 'panelCal.detail.ackFamilyAt' : 'panelCal.detail.ackFamilyAtNoName', params: { time, name: a.by_name ?? '' } });
  } else if (a.result === 'done') {
    out.push({ key: category === 'med' ? 'panelCal.detail.ackDoneMed' : 'panelCal.detail.ackDoneGeneric', params: { time } });
    if (a.via === 'voice') out.push({ key: 'panelCal.detail.ackVoice', params: {} });
  } else {
    out.push({ key: a.result === 'snooze' ? 'panelCal.detail.ackSnooze' : 'panelCal.detail.ackSeen', params: { time } });
    if (a.via === 'voice') out.push({ key: 'panelCal.detail.ackVoice', params: {} });
  }
  if (a.pending) out.push({ key: 'panelCal.detail.ackPending', params: {} });
  return out;
}

/** 同一件事（chain_id；沒有就 series_id ?? event_id）在目前讀到的範圍裡有確認的那幾次，新的在前（§5.3 v0.6） */
export function ackHistory(occs: CalOccurrence[], o: CalOccurrence, max = 10): CalOccurrence[] {
  const key = (x: CalOccurrence) => x.chain_id ?? x.series_id ?? x.event_id;
  const k = key(o);
  return occs
    .filter((x) => key(x) === k && x.ack)
    .sort((a, b) => (b.ack?.at ?? '').localeCompare(a.ack?.at ?? '') || b.start.localeCompare(a.start))
    .slice(0, max);
}

/** 從通知點進來：?event=&rid= 找到那一次（event_id / series_id 都對；找不到回 null） */
export function findOccurrence(occs: CalOccurrence[], eventId: string, recurrenceId?: string): CalOccurrence | null {
  const hit = occs.filter((x) => x.event_id === eventId || x.series_id === eventId || x.chain_id === eventId);
  if (!hit.length) return null;
  if (recurrenceId) return hit.find((x) => x.recurrence_id === recurrenceId) ?? null;
  return hit[0];
}

/** ask_family 點開：用長輩說的新時間 / 新標題預填編輯表單（§7.2 ［看行事曆］直接開那件的編輯畫面） */
export function prefillDraft(d: CalDraft, newStart?: string, newTitle?: string): CalDraft {
  const out = { ...d };
  if (newTitle && newTitle.trim()) out.title = newTitle.trim().slice(0, 40);
  if (newStart && /^\d{4}-\d{2}-\d{2}/.test(newStart)) {
    out.date = newStart.slice(0, 10);
    if (!out.allDay && newStart.length >= 16) out.time = newStart.slice(11, 16);
  }
  return out;
}
